import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import { Download, ShieldAlert, Users, Wallet } from 'lucide-react';
import Layout from '@/components/Layout';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { ACCEPT_AFFILIATE_TERMS, MY_AFFILIATE_STATUS } from '@/queries/queries';
import { CustomLinkEditor } from '@/components/affiliates/CustomLinkEditor';
import { ReferralLinkRow } from '@/components/affiliates/ReferralLinkRow';
import { ReferredUsersCard } from '@/components/affiliates/ReferredUsersCard';
import { buildReferralUrl } from '@/lib/affiliates/buildReferralUrl';
import { VIRALIZE_PROMO_MATERIALS_URL, hasPromoMaterials } from '@/lib/affiliates/promoMaterials';
import { deriveAffiliateProgramView } from '@/lib/affiliates/deriveAffiliateProgramView';
import { deriveEarningDisplayStatus } from '@/lib/affiliates/deriveEarningDisplayStatus';
import { AFFILIATE_DEFAULT_RATES } from '@/lib/affiliates/parseAffiliateRateField';
import { formatCents } from '@/lib/format/currency';
import { WalletStatement } from '@/components/wallet/WalletStatement';
import { WithdrawableBalanceCard } from '@/components/wallet/WithdrawableBalanceCard';
import { formatStatementDateTime } from '@/lib/wallet/formatStatementDates';
import type { MyAffiliateStatus } from '@/generated/graphql';

export const AffiliatesPage = () => {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [copiedLink, setCopiedLink] = useState<'app' | 'viralize' | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [visibleEarnings, setVisibleEarnings] = useState(50);

  const { data, loading, error, refetch } = useQuery<{ myAffiliateStatus: MyAffiliateStatus }>(
    MY_AFFILIATE_STATUS,
  );
  const [acceptTerms, { loading: joining }] = useMutation(ACCEPT_AFFILIATE_TERMS);

  const status = data?.myAffiliateStatus;
  const view = deriveAffiliateProgramView({
    joined: status?.joined,
    blocked: status?.blocked,
  });

  // Effective rates come from the server with defaults already resolved, so a
  // custom-rate affiliate never reads a percentage that contradicts their
  // earnings. The fallback only covers the pre-load render.
  const rates = status?.rates ?? AFFILIATE_DEFAULT_RATES;

  const handleJoin = async () => {
    if (!status) return;
    try {
      await acceptTerms({ variables: { version: status.terms_current_version } });
      await refetch();
    } catch {
      setErrorMessage(t('affiliates.error'));
    }
  };

  const activeCode = status?.custom_code || status?.code || '';
  const referralUrl = activeCode ? buildReferralUrl(activeCode) : '';
  const originalUrl = status?.code ? buildReferralUrl(status.code) : '';
  // Same code, Viralize landing: its guest checkout stamps the code on the
  // purchase, so the course sale is attributed even for a logged-out buyer.
  const viralizeUrl = activeCode ? buildReferralUrl(activeCode, 'viralize') : '';

  const handleCopy = async (key: 'app' | 'viralize', url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedLink(key);
      toast({ title: t('affiliates.link.copied') });
      setTimeout(() => setCopiedLink((current) => (current === key ? null : current)), 2000);
    } catch {
      setErrorMessage(t('affiliates.error'));
    }
  };

  const statusLabel: Record<string, string> = {
    pending: t('affiliates.earnings.statusPending'),
    available: t('affiliates.earnings.statusAvailable'),
    reversed: t('affiliates.earnings.statusReversed'),
  };
  const statusVariant: Record<string, 'secondary' | 'default' | 'destructive'> = {
    pending: 'secondary',
    available: 'default',
    reversed: 'destructive',
  };
  const sourceLabel: Record<string, string> = {
    payment: t('affiliates.earnings.sourcePayment'),
    course: t('affiliates.earnings.sourceCourse'),
    cpm: t('affiliates.earnings.sourceCpm'),
  };
  const now = new Date();

  if (loading && !status) {
    return (
      <Layout>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 pb-24 lg:pb-8 overflow-y-auto">
          <div className="max-w-5xl mx-auto space-y-6">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-20 w-full" />
          </div>
        </main>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 pb-24 lg:pb-8 overflow-y-auto">
          <p className="text-destructive">{t('affiliates.loadError')}</p>
        </main>
      </Layout>
    );
  }

  return (
    <Layout>
      <main className="flex-1 p-4 sm:p-6 lg:p-8 pb-24 lg:pb-8 overflow-y-auto">
        <div className="max-w-5xl mx-auto space-y-6">
          <div>
            <h1 className="text-2xl font-bold">{t('affiliates.title')}</h1>
            <p className="text-muted-foreground">{t('affiliates.subtitle', { payment: rates.payment, course: rates.course, cpm: rates.cpm })}</p>
          </div>

          {view === 'join' ? (
            <Card>
              <CardHeader>
                <CardTitle>{t('affiliates.join.heading')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <ul className="list-disc pl-5 space-y-1 text-sm">
                  <li>{t('affiliates.join.bullet1', { payment: rates.payment })}</li>
                  <li>
                    {t('affiliates.join.bulletCourse', { course: rates.course })}
                  </li>
                  <li>{t('affiliates.join.bulletCpm', { cpm: rates.cpm })}</li>
                  <li>{t('affiliates.join.bullet2')}</li>
                  <li>{t('affiliates.join.bullet3')}</li>
                </ul>
                <div className="rounded-md border p-4 text-sm text-muted-foreground">
                  <p className="font-medium text-foreground mb-2">{t('affiliates.join.termsTitle')}</p>
                  <p>{t('affiliates.join.termsBody', { payment: rates.payment, course: rates.course, cpm: rates.cpm })}</p>
                </div>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox
                    checked={termsAccepted}
                    onCheckedChange={(v) => setTermsAccepted(v === true)}
                    data-testid="affiliate-terms-checkbox"
                  />
                  {t('affiliates.join.accept')}
                </label>
                <Button
                  disabled={!termsAccepted || joining}
                  onClick={handleJoin}
                  data-testid="affiliate-join-button"
                >
                  {joining ? t('affiliates.join.joining') : t('affiliates.join.button')}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              {view === 'blocked' && (
                <Alert variant="destructive" data-testid="affiliate-blocked-notice">
                  <ShieldAlert className="h-4 w-4" />
                  <AlertTitle>{t('affiliates.blocked.title')}</AlertTitle>
                  <AlertDescription className="space-y-1">
                    <p>{t('affiliates.blocked.body')}</p>
                    {status?.blocked_reason ? (
                      <p>{t('affiliates.blocked.reason', { reason: status.blocked_reason })}</p>
                    ) : null}
                  </AlertDescription>
                </Alert>
              )}

              {view === 'member' && (
                <>
                  <Card>
                    <CardHeader>
                      <CardTitle>{t('affiliates.link.title')}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="space-y-2">
                        <ReferralLinkRow
                          url={referralUrl}
                          copied={copiedLink === 'app'}
                          onCopy={() => handleCopy('app', referralUrl)}
                          testIdPrefix="affiliate-referral"
                          label={t('affiliates.link.appLabel')}
                          badges={[
                            t('affiliates.link.appPerPayment', { payment: rates.payment }),
                            t('affiliates.link.appPerCpm', { cpm: rates.cpm }),
                          ]}
                        />
                        {status.custom_code && (
                          <p className="text-xs text-muted-foreground" data-testid="affiliate-original-url">
                            {t('affiliates.link.alsoWorks')} {originalUrl}
                          </p>
                        )}
                        <CustomLinkEditor customCode={status.custom_code ?? null} onSaved={() => refetch()} />
                        <p className="text-sm text-muted-foreground">{t('affiliates.link.hint')}</p>
                      </div>

                      <ReferralLinkRow
                        url={viralizeUrl}
                        copied={copiedLink === 'viralize'}
                        onCopy={() => handleCopy('viralize', viralizeUrl)}
                        testIdPrefix="affiliate-viralize-referral"
                        label={t('affiliates.link.viralizeLabel')}
                        badges={[t('affiliates.link.viralizePerSale', { course: rates.course })]}
                        hint={t('affiliates.link.viralizeHint', { course: rates.course })}
                      />
                    </CardContent>
                  </Card>

                  {hasPromoMaterials() && (
                    <Card>
                      <CardHeader>
                        <CardTitle>{t('affiliates.materials.title')}</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <p className="text-sm text-muted-foreground">{t('affiliates.materials.description')}</p>
                        <Button asChild variant="secondary" data-testid="affiliate-materials-button">
                          <a
                            href={VIRALIZE_PROMO_MATERIALS_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Download className="w-4 h-4" />
                            <span className="ml-2">{t('affiliates.materials.download')}</span>
                          </a>
                        </Button>
                      </CardContent>
                    </Card>
                  )}
                </>
              )}

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {(
                  [
                    { label: t('affiliates.stats.referred'), value: String(status.total_referred), icon: Users },
                    {
                      label: t('affiliates.stats.earned'),
                      value: formatCents(status.totals.total_earned_cents, 'BRL'),
                      icon: Wallet,
                      sub: t('affiliates.stats.breakdown', {
                        payment: formatCents(status.totals.payment_cents, 'BRL'),
                        course: formatCents(status.totals.course_cents, 'BRL'),
                        cpm: formatCents(status.totals.cpm_cents, 'BRL'),
                      }),
                    },
                    { label: t('affiliates.stats.pending'), value: formatCents(status.totals.pending_cents, 'BRL'), icon: Wallet },
                    { label: t('affiliates.stats.available'), value: formatCents(status.totals.released_cents, 'BRL'), icon: Wallet },
                  ] as Array<{ label: string; value: string; icon: typeof Users; sub?: string }>
                ).map((s) => (
                  <Card key={s.label}>
                    <CardContent className="pt-6">
                      <div className="text-sm text-muted-foreground">{s.label}</div>
                      <div className="text-xl font-bold">{s.value}</div>
                      {'sub' in s && s.sub && (
                        <div className="text-xs text-muted-foreground mt-1">{s.sub}</div>
                      )}
                    </CardContent>
                  </Card>
                ))}
                <WithdrawableBalanceCard />
              </div>

              <ReferredUsersCard referredUsers={status.referred_users} totalReferred={status.total_referred} />

              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>{t('affiliates.earnings.title')}</CardTitle>
                  <Button asChild variant="outline" size="sm">
                    <Link to="/earn/wallet">{t('affiliates.wallet.goToWallet')}</Link>
                  </Button>
                </CardHeader>
                <CardContent>
                  {status.earnings.length === 0 ? (
                    <p className="text-sm text-muted-foreground">{t('affiliates.earnings.empty')}</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('affiliates.earnings.date')}</TableHead>
                          <TableHead>{t('affiliates.earnings.referredUser')}</TableHead>
                          <TableHead>{t('affiliates.earnings.source')}</TableHead>
                          <TableHead>{t('affiliates.earnings.paymentAmount')}</TableHead>
                          <TableHead>{t('affiliates.earnings.commission')}</TableHead>
                          <TableHead>{t('affiliates.earnings.releasesAt')}</TableHead>
                          <TableHead>{t('affiliates.earnings.status')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {status.earnings.slice(0, visibleEarnings).map((e) => {
                          const display = deriveEarningDisplayStatus({
                            status: e.status,
                            paymentDate: e.payment_date ? new Date(e.payment_date) : null,
                            createdAt: new Date(e.createdAt),
                            releasesAt: e.releases_at ? new Date(e.releases_at) : null,
                            now,
                          });
                          return (
                            <TableRow key={e.id}>
                              <TableCell>
                                {formatStatementDateTime(e.payment_date || e.createdAt, i18n.language)}
                              </TableCell>
                              <TableCell>{e.referred_user_name || '—'}</TableCell>
                              <TableCell>
                                <Badge variant="outline">{sourceLabel[e.source] ?? e.source}</Badge>
                              </TableCell>
                              <TableCell>
                                {e.source === 'cpm' ? '—' : formatCents(e.payment_amount_cents, 'BRL')}
                              </TableCell>
                              <TableCell className="font-medium">{formatCents(e.amount_cents, 'BRL')}</TableCell>
                              <TableCell className="whitespace-nowrap">
                                {e.releases_at ? formatStatementDateTime(e.releases_at, i18n.language) : '—'}
                              </TableCell>
                              <TableCell>
                                <Badge variant={statusVariant[display]}>{statusLabel[display]}</Badge>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                  {status.earnings.length > visibleEarnings && (
                    <div className="mt-3 flex justify-center">
                      <Button variant="outline" size="sm" onClick={() => setVisibleEarnings((v) => v + 50)}>
                        {t('affiliates.earnings.showMore')}
                      </Button>
                    </div>
                  )}
                  <p className="text-sm text-muted-foreground mt-4">{t('affiliates.wallet.hint')}</p>
                </CardContent>
              </Card>

              <WalletStatement title={t('walletStatement.walletTitle')} />
            </>
          )}

          <Dialog open={!!errorMessage} onOpenChange={() => setErrorMessage(null)}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{errorMessage}</DialogTitle>
              </DialogHeader>
            </DialogContent>
          </Dialog>
        </div>
      </main>
    </Layout>
  );
};

export default AffiliatesPage;
